import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/shared/auth/store';
import { useLogoutOnClose } from '@/shared/auth/useLogoutOnClose';
import { SessionGuard } from '@/shared/auth/SessionGuard';
import { AppRouter } from '@/app/router';
import { Sidebar } from '@/app/Sidebar';
import { UnsavedChangesGuardProvider } from '@/shared/ui/UnsavedChangesGuard';
import { HeldLocksBanner } from '@/shared/ui/HeldLocksBanner';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { ProfileMenu } from '@/app/ProfileMenu';
import { api } from '@/shared/api/client';
import { useSyncDisplayDateFormat } from '@/shared/config/useFeatures';
import { WINDOW_TO_ROUTE, isTestScreensAdmin } from '@/app/nav';
import { registerQuickWindowRoutes } from '@/shared/quickwindow/routeRegistry';
import { installPasteTrim } from '@/shared/ui/pasteTrim';
import { HelpHighlightProvider } from '@/shared/help/HelpHighlightProvider';
import { HelpPanel } from '@/shared/help/HelpPanel';

/**
 * slice-3561 (BigList #24, ADR-0045): hand the Quick Window its PB-window → PH10-route map.
 *
 * `src/shared/` must not import `src/features/` (dep-cruiser `shared-not-from-features`) and
 * has no `shared → app` import anywhere in the tree, so `shared/quickwindow` cannot reach
 * `WINDOW_TO_ROUTE` itself. App already imports every layer, so it injects the map at module
 * scope — before any route renders, and once per SPA load. Unregistered is a legitimate state
 * (an isolated unit test never loads App); the resolver falls back to templates only.
 */
registerQuickWindowRoutes(WINDOW_TO_ROUTE);

/**
 * App shell: top header + left sidebar + main pane. Both header and sidebar
 * are hidden on /login so the login screen can take the full viewport.
 */
export function App() {
  const location = useLocation();
  const onLoginRoute = location.pathname === '/login';
  const userId = useAuthStore((st) => st.session?.userId);
  // Best-effort license release when the tab closes — see useLogoutOnClose for the trade-offs.
  useLogoutOnClose();
  // ADR-0052 (slice-3655): every paste into a text field loses its trailing whitespace and is
  // cut to maxLength — PB's edit.limit over TrimSpaces=1 values. One document listener.
  useEffect(() => installPasteTrim(), []);

  return (
    <UnsavedChangesGuardProvider>
      {/* ADR-0066: living help. One highlight overlay + one docked panel for the whole app. */}
      <HelpHighlightProvider>
        <div className="flex h-full flex-col">
          {!onLoginRoute && <AppHeader />}
          {/* BigList #18: advisory strip, self-hiding unless this session holds inventory locks.
              Also registers the native close prompt while it does. */}
          {!onLoginRoute && <HeldLocksBanner />}
          <div className="flex flex-1 overflow-hidden">
            {!onLoginRoute && <SidebarSlot />}
            <main className="flex-1 overflow-auto">
              {/* {@code h-full} on the route wrapper anchors PageLayout's height chain to
                the viewport. Without it, every `h-full` deeper in the tree resolves to
                "100% of indefinite" → CSS spec collapses it to auto, and any nested
                AG-Grid (which needs a definite parent height for its row viewport) shows
                only its toolbar + status bar with no body. The login route already had
                h-full for its own reasons; everything else gets it now too. */}
              <div className={onLoginRoute ? 'h-full' : 'h-full p-4'}>
                <AppRouter />
              </div>
            </main>
          </div>
          {!onLoginRoute && <StatusStrip />}
          {/* slice-3560 (ADR-0046): the idle-session guard — activity tracking, sliding token
              renewal, the warning countdown, and kiosk auto-renew. Mounted once, and never on
              /login (design doc §5.4: the countdown must not leak into an unauthenticated
              state, and there is nothing to guard there). */}
          {!onLoginRoute && <SessionGuardSlot />}
          {/* ADR-0066: the help panel (F1 / `?` / Help Contents). Portalled + reserves #root's
              right 400px while open, like the test card drawer; never on /login. */}
          {!onLoginRoute && <HelpPanel showWindowIds={isTestScreensAdmin(userId)} />}
        </div>
      </HelpHighlightProvider>
    </UnsavedChangesGuardProvider>
  );
}

/**
 * Bottom status strip -- always-visible BE connection context. FB#51 (CHRIS) rewrite:
 * was showing User / Owner / Warehouse (redundant with the top bar's TenantSwitcher
 * and ProfileMenu); now shows Server: and Database: parsed from spring.datasource.url
 * by AboutController so customers can tell at a glance which environment they're hitting.
 *
 * <p>slice-3555 (BigList item 25 -- "Client mode - add this to the bottom of the screen
 * with the Database/Server info - also making sure the TMS server address if configured is
 * displayed at the bottom as well"): appends {@code Mode:} (PB's {@code gs_mode}, read via
 * {@code AboutController}'s {@code ClientModeReader} -- the SAME source
 * {@code useFeatures().clientMode} reads, so this never drifts from feature-flag gates) and,
 * only when the tenant has a configured TMS connection, {@code TMS:} (the
 * {@code dbxx.ship_connections} address {@code TmsConnectionReader} resolves). The TMS
 * segment is omitted entirely -- not shown as an em-dash -- when unconfigured.
 *
 * <p>BigList #1 (slice-3589) briefly carried the active Warehouse / Owner here after the
 * top-bar TenantSwitcher went; slice-3590 (Chris 2026-09-08) removed that readout too --
 * warehouse/owner are a per-screen concern (ADR-0017/0026), not app chrome. The strip is
 * back to environment facts only: Server / Database / Mode (+ TMS when configured).
 *
 * <p>One GET /api/v1/about on first mount, cached for the session. Hidden until the
 * payload arrives so the footer doesn't briefly flash with empty values.
 */
export function StatusStrip() {
  const session = useAuthStore((s) => s.session);
  const { data } = useQuery<{
    app: { dbServer: string; dbName: string; clientMode?: string; tmsServerAddress?: string | null };
  }>({
    queryKey: ['about', 'footer'],
    queryFn: () => api('/api/v1/about'),
    enabled: session != null,
    staleTime: Infinity, // server + db are set at app boot; never changes during a session
  });
  if (!session) return null;
  const server = data?.app?.dbServer ?? '';
  const database = data?.app?.dbName ?? '';
  const mode = data?.app?.clientMode || 'STD';
  const tmsAddress = data?.app?.tmsServerAddress;
  return (
    <footer className="flex items-center gap-6 border-t border-slate-200 bg-slate-50 px-4 py-1 text-[11px] text-slate-500">
      <span>
        Server:{' '}
        <span className="font-mono text-slate-700">{server || '—'}</span>
      </span>
      <span>
        Database:{' '}
        <span className="font-mono text-slate-700">{database || '—'}</span>
      </span>
      <span>
        Mode: <span className="font-mono text-slate-700">{mode}</span>
      </span>
      {tmsAddress ? (
        <span>
          TMS: <span className="font-mono text-slate-700">{tmsAddress}</span>
        </span>
      ) : null}
    </footer>
  );
}

/** Only mounted with a live session — the hook's timers key off one, and a kiosk display
 *  must not start renewing before it has anything to renew. */
function SessionGuardSlot() {
  const session = useAuthStore((s) => s.session);
  if (!session) return null;
  return <SessionGuard />;
}

/** Sidebar lives inside the provider so its <GuardedNavLink>s see the guard context. */
function SidebarSlot() {
  const session = useAuthStore((s) => s.session);
  if (!session) return null;
  return <Sidebar />;
}

function AppHeader() {
  const session = useAuthStore((s) => s.session);
  // BigList #8 (docs/audits/biglist/08-date-format.md): resolve PB's DisplayDateFormat once
  // per session, mirroring PB's own login-time cascade resolution. AppHeader only renders
  // post-login (App() gates it on !onLoginRoute), so this never fires an unauthenticated
  // GET /api/config/features on the login screen.
  useSyncDisplayDateFormat();
  // Slice-316: Sign Out moved into the new ProfileMenu dropdown alongside Change Password,
  // Productivity Switcher, and About.
  //
  // BigList #1 (Chris 2026-09-04): the top-bar TenantSwitcher is GONE. Warehouse/owner are
  // no longer a global app-chrome mode — every screen whose PB window offers a Whs/Owner
  // picker now renders its own, re-scoping through the validated scope facet (ADR-0026), and
  // screens whose PB window offers none simply run on the session default. The StatusStrip
  // below still DISPLAYS the active pair (read-only) so the operator can always see scope.
  // slice-3794 (ADR-0061): the session's active warehouse/owner SETS are chosen from the
  // profile menu's "Owners/Warehouses" dialog (PB File > Owner / Warehouse); the old
  // single-value switcher (features/auth/scope, POST /api/auth/switch-scope) is removed.
  return (
    <header className="flex items-center gap-4 border-b border-phblue-900 bg-phblue-700 px-4 py-2 text-white">
      {/* Slice-472 (FB#232): PH10 brand is a link to the home landing page. */}
      <Link
        to="/home"
        className="text-base font-semibold text-white hover:text-phblue-100 focus:outline-none focus:ring-2 focus:ring-phblue-300 rounded"
        aria-label="Go to home"
      >
        PH10
      </Link>
      <GlobalSearch />
      <div className="ml-auto flex items-center gap-3 text-xs text-phblue-100">
        {session ? <ProfileMenu /> : <span>not signed in</span>}
      </div>
    </header>
  );
}
